/**
 * BOSS Doğum Günleri sayfası.
 *
 * - Yıl seçici
 * - Yaklaşan doğum günleri (30 gün içinde) — üstte vurgulu
 * - Tüm yıl listesi — sıralı
 * - Inline katsayı seçici (Yok / x1.5 / x2)
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Cake, PartyPopper, RefreshCw, Sparkles, Trash2,
} from 'lucide-react'
import {
  yilKatsayilariGetir, katsayiSetle, katsayiKaldir,
  buYilDogumGunu, dogumGununeKalanGun,
  type DogumKatsayisi, type DogumGunuKatsayisi,
} from '@/lib/services/dogum-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel, IsoTarih } from '@/types'

function tarihEtiketi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })
}

function gunAdi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { weekday: 'long' })
}

interface PersonelDogumGunu {
  personel: KullaniciPersonel
  dogumGunuIso: IsoTarih
  kalanGun: number
  katsayiKaydi: DogumGunuKatsayisi | null
}

export function DogumGunleriPage() {
  const { firebaseUser } = useAuth()
  const buYil = new Date().getFullYear()
  const [yil, setYil] = useState(buYil)
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [katsayilar, setKatsayilar] = useState<Map<string, DogumGunuKatsayisi>>(new Map())
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [isleniyor, setIsleniyor] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      const [pList, kList] = await Promise.all([
        tumPersonelleriGetir(),
        yilKatsayilariGetir(yil),
      ])
      setPersoneller(pList.filter((p) => p.aktif))
      const m = new Map<string, DogumGunuKatsayisi>()
      kList.forEach((k) => m.set(k.personelId, k))
      setKatsayilar(m)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Veriler yüklenemedi')
    } finally {
      setYukleniyor(false)
    }
  }, [yil])

  useEffect(() => { yukle() }, [yukle])

  const liste: PersonelDogumGunu[] = useMemo(() => {
    return personeller
      .filter((p) => p.dogumTarihi)
      .map((p) => {
        const dgIso = buYilDogumGunu(p.dogumTarihi!, yil)
        const kalan = yil === buYil ? dogumGununeKalanGun(p.dogumTarihi!) : -999
        return {
          personel: p,
          dogumGunuIso: dgIso,
          kalanGun: kalan,
          katsayiKaydi: katsayilar.get(p.id) ?? null,
        }
      })
      .sort((a, b) => a.dogumGunuIso.localeCompare(b.dogumGunuIso))
  }, [personeller, katsayilar, yil, buYil])

  const yaklasan = liste.filter((x) => x.kalanGun >= 0 && x.kalanGun <= 30)
  const digerleri = liste.filter((x) => !(x.kalanGun >= 0 && x.kalanGun <= 30))

  async function katsayiDegistir(personelId: string, deger: string) {
    if (!firebaseUser) return
    setIsleniyor(personelId)
    try {
      if (deger === 'yok') {
        await katsayiKaldir(personelId, yil)
      } else {
        await katsayiSetle(
          personelId,
          yil,
          Number(deger) as DogumKatsayisi,
          firebaseUser.uid
        )
      }
      await yukle()
    } catch (err) {
      alert('İşlem başarısız: ' + (err as Error).message)
    } finally {
      setIsleniyor(null)
    }
  }

  const yillar = [buYil - 1, buYil, buYil + 1]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Cake className="w-6 h-6 text-pink-600" />
            Doğum Günleri
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Personellerin doğum gününde mesaileri için özel katsayı belirle
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={yil}
            onChange={(e) => setYil(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-semibold"
          >
            {yillar.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={yukle}
            disabled={yukleniyor}
            className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${yukleniyor ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      <div className="bg-pink-50 border border-pink-200 rounded-xl p-3 flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-pink-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-pink-900">
          <strong>Katsayı atadığında</strong>: Personel o gün giriş yaptığında ekranda
          konfeti animasyonu ve özel mesaj görür. O günkü mesai katsayı ile çarpılarak
          maaşa yansır (örn. x2 = çifte ücret).
        </p>
      </div>

      {/* Yaklaşan doğum günleri */}
      {yil === buYil && yaklasan.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-pink-600" />
            Yaklaşan ({yaklasan.length})
          </h2>
          <div className="space-y-2">
            {yaklasan.map((x) => (
              <DogumGunuSatiri
                key={x.personel.id}
                veri={x}
                vurgulu
                isleniyor={isleniyor === x.personel.id}
                onKatsayiDegistir={(deger) => katsayiDegistir(x.personel.id, deger)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Diğerleri */}
      {digerleri.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            {yil === buYil ? 'Diğer Doğum Günleri' : `${yil} Tüm Doğum Günleri`}
          </h2>
          <div className="space-y-2">
            {digerleri.map((x) => (
              <DogumGunuSatiri
                key={x.personel.id}
                veri={x}
                isleniyor={isleniyor === x.personel.id}
                onKatsayiDegistir={(deger) => katsayiDegistir(x.personel.id, deger)}
              />
            ))}
          </div>
        </div>
      )}

      {!yukleniyor && liste.length === 0 && (
        <div className="card text-center py-12">
          <Cake className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Doğum tarihi tanımlı personel yok</p>
          <p className="text-sm text-gray-500 mt-1">
            Personel kayıtlarında doğum tarihi alanını doldur.
          </p>
        </div>
      )}
    </div>
  )
}

interface SatirProps {
  veri: PersonelDogumGunu
  vurgulu?: boolean
  isleniyor: boolean
  onKatsayiDegistir: (deger: string) => void
}

function DogumGunuSatiri({ veri, vurgulu, isleniyor, onKatsayiDegistir }: SatirProps) {
  const { personel, dogumGunuIso, kalanGun, katsayiKaydi } = veri
  const bugun = kalanGun === 0
  const yarin = kalanGun === 1
  const seciliDeger = katsayiKaydi ? String(katsayiKaydi.katsayi) : 'yok'

  return (
    <div className={`card flex items-center justify-between gap-3 ${
      bugun ? 'bg-gradient-to-r from-pink-50 to-purple-50 border-pink-300 ring-2 ring-pink-200'
        : vurgulu ? 'border-pink-200'
        : ''
    }`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {bugun ? (
          <div className="text-2xl">🎂</div>
        ) : (
          <Cake className={`w-6 h-6 flex-shrink-0 ${
            yarin ? 'text-pink-500' : 'text-gray-400'
          }`} />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 truncate">{personel.isim}</p>
          <p className="text-sm text-gray-600">
            {tarihEtiketi(dogumGunuIso)} · {gunAdi(dogumGunuIso)}
            {bugun && <span className="ml-2 text-pink-700 font-bold">🎉 BUGÜN!</span>}
            {yarin && <span className="ml-2 text-pink-700 font-semibold">Yarın</span>}
            {kalanGun > 1 && kalanGun <= 30 && (
              <span className="ml-2 text-gray-500">· {kalanGun} gün</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <select
          value={seciliDeger}
          onChange={(e) => onKatsayiDegistir(e.target.value)}
          disabled={isleniyor}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-semibold bg-white"
        >
          <option value="yok">Katsayı Yok</option>
          <option value="1.5">x1.5</option>
          <option value="2">x2</option>
        </select>
        {katsayiKaydi && (
          <button
            onClick={() => onKatsayiDegistir('yok')}
            disabled={isleniyor}
            className="p-1.5 hover:bg-red-100 rounded-lg text-gray-500 hover:text-red-600"
            title="Kaldır"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
